import { CTA_BUTTON_COPY, CTA_LINK_3 } from "components/data/landing-page-data";
import AllInOneF from "components/landing-pages/surveillance/version-f/AllInOneF";
import CaseStudiesF from "components/landing-pages/surveillance/version-f/CaseStudiesF";
import FormSectionF from "components/landing-pages/surveillance/version-f/FormSectionF";
import HeroF from "components/landing-pages/surveillance/version-f/HeroF";
import HighlightsF from "components/landing-pages/surveillance/version-f/HighlightsF";
import PlatformF from "components/landing-pages/surveillance/version-f/PlatformF";
import GlobalLayout from "components/page/GlobalLayout";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

import icon1 from "components/landing-pages/icons/bandwidth.svg";
import icon2 from "components/landing-pages/icons/handyman.svg";
import icon3 from "components/landing-pages/icons/system.svg";
import icon4 from "components/landing-pages/icons/security.svg";

const EXPERIMENTAL_FOOTER_MENUS = [
  {
    title: "LEARN MORE",
    links: [{ title: "Contact", path: "/lp/cta-3/" }],
  },
  {
    title: "PRODUCTS",
    links: [
      { title: "Surveillance", path: "/lp/surveillance-f/" },
      { title: "Security Monitoring", path: "/lp/security-a/" },
    ],
  },
];

const CamerasFLandingPageAus = () => {
  const imageData = useStaticQuery(graphql`
    query {
      heroImage: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/cameras-f-hero.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH)
        }
      }
      logoImage: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/cameras-aus-logos.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH)
        }
      }
      card1: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/card-1.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card2: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/card-2.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card3: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/card-3.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card4: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/card-4.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }

      camera1: file(
        relativePath: { eq: "components/landing-pages/img/cameras-f/dome.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      camera2: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/fisheye.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      camera3: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/bullet.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      camera4: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/multisensor.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }

      caseStudy1: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/metasla.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo1: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/metasla-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudy2: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/middlebury.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo2: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/middlebury-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudy3: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/storquest.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      caseStudyLogo3: file(
        relativePath: {
          eq: "components/landing-pages/img/cameras-f/storquest-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  return (
    <GlobalLayout
      landingPage
      useExperimentalFooter
      includeButton
      buttonText={CTA_BUTTON_COPY}
      ctaLink={CTA_LINK_3}
      color="var(--secondary-bg)"
      expirementalFooterMenus={EXPERIMENTAL_FOOTER_MENUS}
    >
      <Helmet>
        <title>Rhombus Cameras</title>
        <meta
          name="description"
          content="Discover the power of cloud-managed security cameras."
        />
      </Helmet>
      <HeroF
        title="Smart Security Cameras with AI Analytics and Onboard Storage"
        blurb="Scale quickly with cloud-connected PoE cameras. Transform security operations with Rhombus cameras for every need. Discover how our cloud-managed platform improves threat deterrence and response with facial recognition, license plate detection, real-time alerts, and more."
        logoImage={imageData.logoImage}
        heroImage={imageData.heroImage}
      />
      <HighlightsF
        stats={[
          { title: "99.9%", subTitle: "Platform Uptime for Unlimited Devices" },
          { title: "120K+", subTitle: "Cameras Deployed Globally" },
          { title: "3M+", subTitle: "Video Events Analysed Daily" },
        ]}
        iconBoxes={[
          {
            icon: { src: icon1 },
            text: "High bandwidth consumption that overwhelms your network",
          },
          {
            icon: { src: icon2 },

            text: "Complex installs, unnecessary hardware, and added costs",
          },
          {
            icon: { src: icon3 },

            text: "Device and connectivity issues leaving critical operations blind",
          },
          {
            icon: { src: icon4 },

            text: "Cybersecurity and port-forwarding risks",
          },
        ]}
        title="Commercial Cameras Shouldn't Be Cumbersome"
      />
      <AllInOneF
        title="Plug-and-Play Cameras for Reliable and Secure Operations"
        bgColor="var(--nuetral-100)"
        cardColor="var(--white)"
        showTestimonial
        showCTA
        cards={[
          {
            image: imageData.camera1,
            title: "Dome",
            blurb:
              "Expand coverage across diverse environments with versatile dome enterprise security cameras. ",
          },
          {
            image: imageData.camera2,
            title: "Fisheye",
            blurb:
              "Monitor large areas with 360 security camera for panoramic and immersive views.",
          },
          {
            image: imageData.camera3,
            title: "Bullet",
            blurb:
              "Capture crystal-clear details at distance, even in harsh conditions with bullet surveillance cameras. ",
          },
          {
            image: imageData.camera4,
            title: "Multisensor",
            blurb:
              "Eliminate blind spots with 20MP commercial security camera. Four sensors for complete visibility.",
          },
        ]}
      />
      <PlatformF
        title="Powerful Protection, Zero Hassle"
        bgColor="var(--white)"
        cardColor="var(--nuetral-100)"
        cardBgColor="var(--primary-bg)"
        showTestimonial={false}
        cards={[
          {
            image: imageData.card1,
            title: "Onboard Analytics",
            blurb:
              "Crystal-clear footage, lighting fast processing, and AI analytics right out of the box.",
          },
          {
            image: imageData.card2,
            title: "Bandwidth Friendly",
            blurb:
              "50% less bandwidth and 10x better latency compared to other cloud IP cameras.",
          },
          {
            image: imageData.card3,
            title: "Secure Remote Access",
            blurb:
              "See and manage footage and devices anytime, anywhere from a central platform that prioritises cybersecurity.",
          },
          {
            image: imageData.card4,
            title: "Simplified Operations",
            blurb:
              "Set up in minutes and gain unlimited scalability, automatic updates, and industry leading warranties.",
          },
        ]}
      />
      <CaseStudiesF
        title="Improving Visibility for Organisations Like Yours"
        caseStudies={[
          {
            image: imageData.caseStudy1,
            stats: [
              { title: "80", subTitle: "hours saved weekly" },
              { title: "700,000", subTitle: "square feet" },
            ],
            quote: (
              <>
                For every camera we've had,{" "}
                <b>Rhombus saves us thousands of dollars</b> each month in terms
                of diagnosing issues preemptively, catching things before they
                go wrong, and preventing downtime.
              </>
            ),
            logo: { src: imageData.caseStudyLogo1, height: 36, width: 132 },
          },
          {
            image: imageData.caseStudy2,
            stats: [
              { title: "70%", subTitle: "time savings" },
              { title: "80%", subTitle: "OUTAGES DECREASE" },
            ],
            quote: (
              <>
                With Rhombus' advanced search features and motion detection
                event markers, we've reduced the time it takes to investigate
                incidents by 70%.{" "}
                <b>
                  What used to take hours of reviewing footage now takes
                  minutes.
                </b>
              </>
            ),
            logo: { src: imageData.caseStudyLogo2, height: 36, width: 183 },
          },
          {
            image: imageData.caseStudy3,
            stats: [
              { title: "30%", subTitle: "Less break-ins" },
              { title: "90%", subTitle: "faster response" },
            ],
            quote: (
              <>
                With Rhombus' proactive monitoring and real-time alert system,{" "}
                <b>
                  local authorities are able to respond to trespassing incidents
                  within as little as 5 minutes of detection
                </b>
                , a game-changing 90% faster response.
              </>
            ),
            logo: { src: imageData.caseStudyLogo3, height: 36, width: 129 },
          },
        ]}
      />
      <FormSectionF
        checkList={["Custom platform demo", "Customised Free trial", "Pricing"]}
      />
    </GlobalLayout>
  );
};

export default CamerasFLandingPageAus;
